import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ShadowSheetWrapper } from "./ShadowSheetWrapper";
import { useScrollLock, useFocusTrap, useSheetAnimation } from "../hooks";
import {
  createBackdropStyles,
  createSheetWrapperStyles,
} from "../utils/sheet-styles";

export interface SustSheetProps {
  open: boolean;
  onClose: () => void;
  side: "end" | "bottom";
  children: React.ReactNode;
  className?: string;
  cssContent: string;
  style?: React.CSSProperties;
  sheetRef?: React.RefObject<HTMLDivElement>;
}

export const SustSheet: React.FC<SustSheetProps> = ({
  open,
  onClose,
  side,
  children,
  className = "",
  cssContent,
  style,
  sheetRef,
}) => {
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);
  const sheetContainerRef = sheetRef || useRef<HTMLDivElement>(null);

  // State to store locked height for mobile bottom sheets
  const [lockedHeight, setLockedHeight] = useState<number | undefined>();
  const sheetWrapperRef = useRef<HTMLDivElement>(null);

  const { isVisible, isAnimating } = useSheetAnimation({ open });

  useScrollLock({ enabled: open });

  useFocusTrap({
    enabled: open && isAnimating,
    containerRef: sheetContainerRef,
    shadowRoot: shadowRoot,
    onEscape: onClose,
    initialFocusSelector: "#sheet-close-button",
  });

  // Capture and lock height for mobile bottom sheets after main view renders
  useEffect(() => {
    if (side !== "bottom") return;

    if (isVisible && !lockedHeight && sheetWrapperRef.current) {
      const timeoutId = setTimeout(() => {
        if (sheetWrapperRef.current) {
          const height = sheetWrapperRef.current.getBoundingClientRect().height;
          if (height > 0) {
            setLockedHeight(height);
          }
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    } else if (!open) {
      setLockedHeight(undefined);
    }
  }, [isVisible, side, lockedHeight, open]);

  const sheetClasses = [
    "sust-sheet",
    `sust-sheet--${side}`,
    isAnimating && "sust-sheet--open",
    !isVisible && "sust-sheet--hidden",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Generate inline styles using utility functions
  const backdropWrapperStyle = createBackdropStyles({
    isVisible,
    isAnimating,
  });

  const sheetWrapperStyle = createSheetWrapperStyles({
    side,
    isVisible,
    isAnimating,
    customStyle: {
      ...style,
      ...(side === "bottom" && lockedHeight
        ? {
            height: `${lockedHeight}px`,
            maxHeight: "70vh",
          }
        : {}),
    },
  });

  // Handle backdrop click - only close if clicking the backdrop itself
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const content = (
    <>
      {/* Light DOM backdrop wrapper - renders behind sheet */}
      {isVisible && (
        <div style={backdropWrapperStyle} onClick={handleBackdropClick} />
      )}

      {/* Light DOM sheet wrapper - renders on top of backdrop */}
      {isVisible && (
        <div ref={sheetWrapperRef} style={sheetWrapperStyle}>
          <ShadowSheetWrapper
            cssContent={cssContent}
            className={className}
            sheetClasses={sheetClasses}
            sheetStyle={{}}
            sheetRef={sheetContainerRef}
            onShadowRootReady={setShadowRoot}
            isOpen={open}
          >
            {React.Children.map(children, (child) => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, { shadowRoot } as Record<
                  string,
                  unknown
                >);
              }
              return child;
            })}
          </ShadowSheetWrapper>
        </div>
      )}
    </>
  );

  // Use portal to attach directly to document.body for proper stacking context
  return createPortal(content, document.body);
};
